import React from 'react';
import { Mail, Phone, Facebook, Instagram, MapPin } from 'lucide-react';
import { trackSocialClick, trackContactEvent } from '../utils/analytics';
import CookieSettings from './CookieSettings';
import GoogleRating from './GoogleRating';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <div className="space-y-4 sm:space-y-6 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start space-x-3">
              <img 
                src="/logo.png" 
                alt="Fliesen Jarek Logo" 
                className="h-10 sm:h-12 w-auto"
              />
              <img 
                src="/Textlogo.png" 
                alt="Fliesen Jarek" 
                className="h-6 sm:h-8 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Ihr Experte für hochwertige Fliesenarbeiten in Cuxhaven und Umgebung. 
              Professionelle Handwerkskunst seit über 20 Jahren.
            </p>
            <div className="mb-4">
              <GoogleRating compact={true} />
            </div>
            {/* Social Media */}
            <div className="flex justify-center sm:justify-start space-x-4">
              <a 
                href="https://www.facebook.com/fliesenjarek"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('facebook')}
                className="bg-white/10 p-2 rounded-full hover:bg-primary-600 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.instagram.com/fliesenjarek"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSocialClick('instagram')}
                className="bg-white/10 p-2 rounded-full hover:bg-primary-600 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
        </div>

          {/* Quick Links */}
          <div className="text-center sm:text-left">
            <h4 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Schnellzugriff
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-primary-500 mt-2"></span>
            </h4>
            <ul className="space-y-3">
              <li className="flex justify-center sm:justify-start">
                <a href="#services" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center group">
                  <span className="mr-2 text-primary-500 group-hover:translate-x-1 transition-transform">›</span>
                  Leistungen
                </a>
              </li>
              <li className="flex justify-center sm:justify-start">
                <a href="#about" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center group">
                  <span className="mr-2 text-primary-500 group-hover:translate-x-1 transition-transform">›</span>
                  Über uns
                </a>
              </li>
              <li className="flex justify-center sm:justify-start">
                <a href="#gallery" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center group">
                  <span className="mr-2 text-primary-500 group-hover:translate-x-1 transition-transform">›</span>
                  Projekte
                </a>
              </li>
              <li className="flex justify-center sm:justify-start">
                <a href="#testimonials" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center group">
                  <span className="mr-2 text-primary-500 group-hover:translate-x-1 transition-transform">›</span>
                  Referenzen
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="text-center sm:text-left">
            <h4 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Unsere Services
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-primary-500 mt-2"></span>
            </h4>
            <ul className="space-y-3 text-center sm:text-left">
              <li className="text-gray-400 text-sm">Badsanierung</li>
              <li className="text-gray-400 text-sm">Fliesenverlegung</li>
              <li className="text-gray-400 text-sm">Natursteinarbeiten</li>
              <li className="text-gray-400 text-sm">Terrassenbau</li>
              <li className="text-gray-400 text-sm">Reparaturservice</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="text-center sm:text-left">
            <h4 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Kontakt
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-primary-500 mt-2"></span>
            </h4>
            <ul className="space-y-4">
              <li className="flex flex-col sm:flex-row items-center sm:items-start space-y-1 sm:space-y-0 sm:space-x-3">
                <Phone size={18} className="text-primary-500 mb-1 sm:mb-0 sm:mt-0.5" />
                <div className="text-center sm:text-left">
                  <p className="text-white text-sm font-medium">Telefon</p>
                  <a 
                    href="tel:015154610847" 
                    onClick={() => trackContactEvent('phone_footer', '0151 54610847')}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                  0151 54610847
                </a>
                </div>
              </li>
              <li className="flex flex-col sm:flex-row items-center sm:items-start space-y-1 sm:space-y-0 sm:space-x-3">
                <Mail size={18} className="text-primary-500 mb-1 sm:mb-0 sm:mt-0.5" />
                <div className="text-center sm:text-left">
                  <p className="text-white text-sm font-medium">E-Mail</p>
                  <a 
                    href="mailto:jarek.j79@web.de" 
                    onClick={() => trackContactEvent('email_footer', 'jarek.j79@web.de')}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    jarek.j79@web.de
                  </a>
                </div>
              </li>
              <li className="flex flex-col sm:flex-row items-center sm:items-start space-y-1 sm:space-y-0 sm:space-x-3">
                <MapPin size={18} className="text-primary-500 mb-1 sm:mb-0 sm:mt-0.5" />
                <div className="text-center sm:text-left">
                  <p className="text-white text-sm font-medium">Servicegebiet</p>
                  <p className="text-gray-400 text-sm">Cuxhaven & Umgebung</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-xs sm:text-sm text-gray-400 text-center md:text-left">
              © {currentYear} Fliesen Jarek. Alle Rechte vorbehalten.
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-xs sm:text-sm">
              <div className="flex items-center space-x-3 sm:space-x-6">
                <a 
                  href="/impressum"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Impressum
                </a>
                <span className="text-gray-600 hidden sm:inline">|</span>
                <a 
                  href="/datenschutz"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  Datenschutz
                </a>
                <span className="text-gray-600 hidden sm:inline">|</span>
                <a 
                  href="/agb"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  AGB
                </a>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 hidden sm:inline mr-6">|</span>
                <CookieSettings />
              </div>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500 text-center md:text-right">
            Handcrafted by <a href="https://rodriguez-web.de/" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary-500">Rodriguez-Web</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 